/**
 * 관리자 > 문서 라이브러리 — 라우트 admin.library.* 와 동기화.
 *
 * index(): 새로고침 없는 비동기 필터 + 검색 debounce.
 *   - 사이드바 카테고리 클릭 → 선택 state 토글 → 카드 필터 + URL pushState
 *   - 검색 인풋 → debounce 220ms → 필터 + URL pushState
 *   - 활성 필터 칩 클릭 → 해당 카테고리 제거
 *   - 모두 해제 버튼 → 카테고리 전체 해제
 *   - popstate(브라우저 뒤로가기) → URL 다시 파싱해 동기화
 *   - 클립보드 복사 버튼 → raw 엔드포인트 fetch → Toast
 *
 * show(): 단일 문서 — 마크다운 렌더 + 복사 버튼.
 * 마크다운 렌더러는 AdminProjectDocController.ts 와 동일한 인라인 렌더러(SOLID — 외부 의존성 없음).
 *
 * 뷰: resources/views/admin/pages/library/{index,show}.php
 */
import { Logger } from "../../utils/Logger";
import { Toast } from "../../utils/Toast";

const CTRL_TAG = "AdminDocsLibraryController";

/* --------------------------------------------------------------------------
 * 가벼운 마크다운 → HTML 렌더러 (show 페이지 전용)
 * ------------------------------------------------------------------------ */

function escapeHtml(input: string): string {
  return input.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderInline(text: string): string {
  let out = escapeHtml(text);
  const codeSpans: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_m, c: string) => {
    codeSpans.push(c);
    return ` CODE${codeSpans.length - 1} `;
  });
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, "$1<em>$2</em>");
  out = out.replace(/(^|[^_\w])_([^_\s][^_]*?)_(?![_\w])/g, "$1<em>$2</em>");
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label: string, url: string) => {
    const safe = /^(https?:\/\/|mailto:|#|\/|\.\.?\/)/i.test(url) ? url : "#";
    const external = /^https?:\/\//i.test(safe);
    const attrs = external ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `<a href="${safe}"${attrs}>${label}</a>`;
  });
  out = out.replace(/ CODE(\d+) /g, (_m, i: string) => `<code>${codeSpans[Number(i)]}</code>`);
  return out;
}

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
}
function splitRow(line: string): string[] {
  return line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
}

function renderMarkdown(src: string): string {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const html: string[] = [];
  let inCode = false;
  let codeBuf: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let paraBuf: string[] = [];
  const flushPara = (): void => {
    if (paraBuf.length) { html.push(`<p>${renderInline(paraBuf.join(" "))}</p>`); paraBuf = []; }
  };
  const flushList = (): void => { if (listType) { html.push(`</${listType}>`); listType = null; } };
  const flushBlocks = (): void => { flushPara(); flushList(); };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*(```|~~~)/.test(line)) {
      if (inCode) {
        html.push(`<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`);
        codeBuf = []; inCode = false;
      } else {
        flushBlocks(); inCode = true;
      }
      continue;
    }
    if (inCode) { codeBuf.push(line); continue; }
    if (/^\s*$/.test(line)) { flushBlocks(); continue; }
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { flushBlocks(); html.push("<hr>"); continue; }
    const heading = line.match(/^(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (heading) {
      flushBlocks();
      const level = heading[1].length;
      html.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      continue;
    }
    const quote = line.match(/^\s*>\s?(.*)$/);
    if (quote) {
      flushPara(); flushList();
      html.push(`<blockquote>${renderInline(quote[1])}</blockquote>`);
      continue;
    }
    if (line.includes("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushBlocks();
      const headers = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes("|") && !/^\s*$/.test(lines[i])) {
        rows.push(splitRow(lines[i])); i++;
      }
      i--;
      const thead = `<thead><tr>${headers.map((h) => `<th>${renderInline(h)}</th>`).join("")}</tr></thead>`;
      const tbody = `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${renderInline(c)}</td>`).join("")}</tr>`).join("")}</tbody>`;
      html.push(`<table>${thead}${tbody}</table>`);
      continue;
    }
    const ulItem = line.match(/^\s*[-*+]\s+(.*)$/);
    const olItem = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ulItem || olItem) {
      flushPara();
      const want: "ul" | "ol" = ulItem ? "ul" : "ol";
      if (listType && listType !== want) flushList();
      if (!listType) { html.push(`<${want}>`); listType = want; }
      const text = ulItem ? ulItem[1] : (olItem as RegExpMatchArray)[1];
      html.push(`<li>${renderInline(text)}</li>`);
      continue;
    }
    flushList();
    paraBuf.push(line.trim());
  }
  if (inCode) html.push(`<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`);
  flushBlocks();
  return html.join("\n");
}

/* -------------------------------------------------------------------------- */

function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: A) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

async function copyMd(url: string): Promise<void> {
  try {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) { Toast.error("원문을 불러오지 못했습니다."); return; }
    const text = await res.text();
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } finally { ta.remove(); }
    }
    Toast.success("md 원문을 복사했습니다.");
  } catch (e) {
    Logger.debug(`${CTRL_TAG}.copy failed`, e as Error);
    Toast.error("복사에 실패했습니다.");
  }
}

function bindCopyButtons(scope: ParentNode): void {
  scope.querySelectorAll<HTMLElement>("[data-library-copy]").forEach((btn) => {
    if (btn.dataset.libraryCopyBound === "1") return;
    btn.dataset.libraryCopyBound = "1";
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const url = btn.dataset.rawUrl
        || (btn.closest<HTMLElement>("[data-raw-url]")?.dataset.rawUrl)
        || "";
      if (!url) { Toast.error("복사할 URL 을 찾지 못했습니다."); return; }
      void copyMd(url);
    });
  });
}

/* --------------------------------------------------------------------------
 * Index 페이지 — 비동기 필터 컨트롤러 (state + render + URL sync)
 * ------------------------------------------------------------------------ */

type LibraryState = {
  cats: Set<string>;
  q: string;
};

function parseStateFromUrl(url: URL): LibraryState {
  const cats = new Set<string>();
  const catRaw = url.searchParams.get("cat");
  if (catRaw) {
    catRaw.split(",").forEach((s) => {
      const v = s.trim();
      if (v) cats.add(v);
    });
  }
  return { cats, q: (url.searchParams.get("q") || "").trim() };
}

function stateToUrl(base: string, state: LibraryState): string {
  const u = new URL(base, window.location.origin);
  if (state.cats.size > 0) u.searchParams.set("cat", Array.from(state.cats).join(","));
  else u.searchParams.delete("cat");
  if (state.q !== "") u.searchParams.set("q", state.q);
  else u.searchParams.delete("q");
  const qs = u.searchParams.toString();
  return u.pathname + (qs ? "?" + qs : "");
}

function cardMatches(card: HTMLElement, state: LibraryState): boolean {
  if (state.cats.size > 0) {
    const cats = (card.dataset.categories || "").split(",").map((s) => s.trim()).filter(Boolean);
    let hit = false;
    for (const c of cats) {
      if (state.cats.has(c)) { hit = true; break; }
    }
    if (!hit) return false;
  }
  if (state.q !== "") {
    const q = state.q.toLowerCase();
    const title = card.dataset.title || "";
    const desc  = card.dataset.desc  || "";
    const key   = (card.dataset.key  || "").toLowerCase();
    if (!title.includes(q) && !desc.includes(q) && !key.includes(q)) return false;
  }
  return true;
}

export const AdminDocsLibraryController = {
  index(): void {
    const root = document.querySelector<HTMLElement>('[data-controller="AdminDocsLibraryController"][data-action="index"]');
    if (!root) return;
    const routeBase = root.dataset.routeBase || window.location.pathname;

    const grid       = root.querySelector<HTMLElement>("[data-library-grid]");
    const empty      = root.querySelector<HTMLElement>("[data-library-empty]");
    const visCount   = root.querySelector<HTMLElement>("[data-library-visible-count]");
    const chipsWrap  = root.querySelector<HTMLElement>("[data-library-chips]");
    const searchIn   = root.querySelector<HTMLInputElement>("[data-library-search]");
    const searchClr  = root.querySelector<HTMLElement>("[data-library-search-clear]");
    const clearAll   = root.querySelector<HTMLElement>("[data-library-clear]");
    const catBtns    = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-library-cat]"));
    const allBtn     = catBtns.find((b) => b.dataset.libraryCat === "") || null;
    const cards      = Array.from(grid?.querySelectorAll<HTMLElement>("[data-library-card]") || []);

    if (!grid) return;

    const state: LibraryState = parseStateFromUrl(new URL(window.location.href));

    const labelFor = (slug: string): string => {
      const btn = catBtns.find((b) => b.dataset.libraryCat === slug);
      return btn?.querySelector<HTMLElement>(".admin-library__cat-label")?.textContent?.trim() || slug;
    };

    const renderChips = (): void => {
      if (!chipsWrap) return;
      chipsWrap.innerHTML = "";
      if (state.cats.size === 0) {
        chipsWrap.classList.add("is-hidden");
        return;
      }
      chipsWrap.classList.remove("is-hidden");
      state.cats.forEach((slug) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "admin-library__chip";
        btn.dataset.libraryCat = slug;
        btn.innerHTML = `<strong>${escapeHtml(labelFor(slug))}</strong><i class="fa-solid fa-xmark" aria-hidden="true"></i>`;
        btn.addEventListener("click", () => toggleCat(slug));
        chipsWrap.appendChild(btn);
      });
    };

    const syncSidebar = (): void => {
      catBtns.forEach((btn) => {
        const slug = btn.dataset.libraryCat || "";
        const active = slug === "" ? state.cats.size === 0 : state.cats.has(slug);
        btn.classList.toggle("admin-library__cat--active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
      });
      if (clearAll) clearAll.classList.toggle("is-hidden", state.cats.size === 0);
    };

    const applyFilter = (): void => {
      let visible = 0;
      for (const card of cards) {
        const show = cardMatches(card, state);
        card.hidden = !show;
        if (show) visible++;
      }
      if (visCount) visCount.textContent = String(visible);
      if (empty) empty.classList.toggle("is-hidden", visible > 0);
    };

    const pushUrl = (replace = false): void => {
      const href = stateToUrl(routeBase, state);
      if (replace) window.history.replaceState({ libState: true }, "", href);
      else window.history.pushState({ libState: true }, "", href);
    };

    const renderAll = (pushHistory: boolean): void => {
      renderChips();
      syncSidebar();
      applyFilter();
      if (searchClr) searchClr.classList.toggle("is-hidden", state.q === "");
      if (pushHistory) pushUrl();
    };

    const toggleCat = (slug: string): void => {
      if (slug === "") {
        state.cats.clear();
      } else if (state.cats.has(slug)) {
        state.cats.delete(slug);
      } else {
        state.cats.add(slug);
      }
      renderAll(true);
    };

    // 사이드바 카테고리 버튼
    catBtns.forEach((btn) => {
      const slug = btn.dataset.libraryCat || "";
      if (btn.disabled) return;
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        toggleCat(slug);
      });
    });

    // 활성 필터 칩 (초기 SSR markup) — 이벤트 위임 대신 renderChips() 가 새로 만들 때 바인딩
    // 단, 첫 로드에 SSR 칩이 있으면 클릭 핸들러 추가
    if (chipsWrap) {
      chipsWrap.querySelectorAll<HTMLElement>("[data-library-cat]").forEach((btn) => {
        const slug = btn.dataset.libraryCat || "";
        btn.addEventListener("click", () => toggleCat(slug));
      });
    }

    // 모두 해제 버튼
    if (clearAll) {
      clearAll.addEventListener("click", (e) => {
        e.preventDefault();
        state.cats.clear();
        renderAll(true);
      });
    }

    // 검색 (debounce 220ms)
    if (searchIn) {
      const onSearch = debounce(() => {
        state.q = searchIn.value.trim();
        renderAll(true);
      }, 220);
      searchIn.addEventListener("input", onSearch);
      // Enter 키로 즉시 적용
      searchIn.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          state.q = searchIn.value.trim();
          renderAll(true);
        }
      });
    }
    if (searchClr) {
      searchClr.addEventListener("click", (e) => {
        e.preventDefault();
        if (searchIn) searchIn.value = "";
        state.q = "";
        renderAll(true);
      });
    }

    // 브라우저 뒤로가기/앞으로가기
    window.addEventListener("popstate", () => {
      const next = parseStateFromUrl(new URL(window.location.href));
      state.cats = next.cats;
      state.q = next.q;
      if (searchIn) searchIn.value = state.q;
      renderAll(false);
    });

    bindCopyButtons(root);

    // 초기 동기화 (SSR 상태와 state 일치 보장 — flash 없이 그대로)
    if (searchIn) searchIn.value = state.q;
    renderAll(false);

    Logger.debug(`${CTRL_TAG}.index ready (cats=${state.cats.size}, q="${state.q}", cards=${cards.length})`);
  },

  show(): void {
    const source   = document.querySelector<HTMLTextAreaElement>("[data-library-source]");
    const rendered = document.querySelector<HTMLElement>("[data-library-rendered]");
    if (source && rendered) {
      rendered.innerHTML = renderMarkdown(source.value);
    }
    bindCopyButtons(document);
    Logger.debug(`${CTRL_TAG}.show ready`);
  },

  raw(): void {
    Logger.debug(`${CTRL_TAG}.raw (no-op)`);
  },
};
